import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ManagerSettings } from '../../../classes/models/employee/ManagerSettings';
import {
  AddEmployeeManagerSettingsRequest,
  EditEmployeeManagerSettingsRequest
} from '../../../interfaces/request/EmployeeRequests';
import { EmployeePositionId } from '../../../classes/models/employee/Employee';
import { Observable } from 'rxjs';
import { ManagerSettingsAdapter } from '../../../classes/adapters/response/employee/ManagerSettingsAdapter';
import { map } from 'rxjs/operators';
import { EmployeeManagerSettingsResponse } from '../../../interfaces/response/EmployeeResponse';

/**
 * This service communicates with backend to handle employee manager settings data management.
 */
@Injectable({
  providedIn: 'root'
})
export class EmployeeManagerSettingsService {
  private http = inject(HttpClient);
  private managerSettingsAdapter = inject(ManagerSettingsAdapter);

  url = environment.apiUrl + '/employees';

  /**
   * Gets manager settings for general manager or assistant manager.
   * @param id identifiers of employee and position.
   */
  getManagerSettings(id: EmployeePositionId): Observable<ManagerSettings[]> {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/manager-settings`;
    return this.http.get(url).pipe(
      map((data: EmployeeManagerSettingsResponse[]) => this.managerSettingsAdapter.adapt(data)));
  }

  /**
   * Adds new manager settings to the employee.
   * @param id identifiers of employee and position.
   * @param managerSettings settings that apply to the manager.
   */
  addManagerSettings(id: EmployeePositionId, managerSettings: ManagerSettings) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/manager-settings`;
    const request: AddEmployeeManagerSettingsRequest = {
      date: managerSettings.startDate,
      canDoSales: managerSettings.canDoSales
    };
    return this.http.post(url, request);
  }

  /**
   * Edits manager settings to the employee.
   * @param id identifiers of employee and position.
   * @param managerSettings settings that apply to the manager.
   */
  editManagerSettings(id: EmployeePositionId, managerSettings: ManagerSettings) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/manager-settings/${managerSettings.startDate}`;
    const request: EditEmployeeManagerSettingsRequest = {
      canDoSales: managerSettings.canDoSales
    };
    return this.http.put(url, request);
  }

  /**
   * Deletes existing manager settings of the employee.
   * @param id identifiers of employee and position.
   * @param startDate start date from which the setting will be deleted.
   */
  deleteManagerSettings(id: EmployeePositionId, startDate: string) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/manager-settings/${startDate}`;
    return this.http.delete(url);
  }
}
